import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Grid, Typography, Dialog, DialogContent, Link } from "@mui/material/";
import makeStyles from "@mui/styles/makeStyles";
import { strings } from "../../../../common/i18n";
import { enums, format, helpers } from "../../../../common/lib";
import styles from "../styles";
import {
    useImageTypeIdentifier,
    useCheckGroupNamePresent
} from "../../../../common/hooks";
import { Edit } from "@mui/icons-material";
import actions from "../../../../Store/actions";
import UpdateGeoLocation from "./UpdateGeoLocation";
import useBranchName from "./useBranchName";
import useClosestLocation from "./useClosestLocation";
import GridColumn from "../GridColumn";
import AuditServices from "./AuditServices";
import config from "../../../../config";
import ErnestDetail from "./ErnestDetail";
import { logToSentry } from "../../../../config/Sentry";

type Props = {
    auditImage: any
};

/**
 * Audit Image Detail Component
 */
export default ({ auditImage, foundElsewhere }) => {
    const { locations } = useSelector((state) => state);
    const dispatch = useDispatch();
    const { allowedDistanceRange } = config;

    const [locationDialog, setLocationDialog] = useState(false);
    const classes = makeStyles(styles)();
    const params = useParams();

    const { createdDate, latitude, longitude, imageType, services, id } =
        auditImage;

    const imageTypeName = useImageTypeIdentifier(imageType);
    const closestLocation = useClosestLocation(auditImage);
    const branchName = useBranchName(params.auditId);

    /** Display Services for 3 image types
     * 1. Chasis 2. Reg 3. full vehicle*/
    const displayServices = imageType <= 3 ? true : false;

    /** Combine the address and closestLocation name */
    const branchAddress = useMemo(() => {
        if (closestLocation.name) {
            return `${branchName}  ${closestLocation.name}`;
        } else {
            return `${branchName}`;
        }
    }, [closestLocation, branchName]);

    const onCloseDealerLocation = () => setLocationDialog(false);

    /** Update Dealer Locations with current location*/
    const onUpdateDealerLocation = () => {
        try {
            let loc = helpers.getListItemForId(locations, closestLocation.id);
            dispatch(actions.locations.create.itemSet(loc));
            setLocationDialog(true);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On updating dealer location:", { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onUpdateDealerLocation")
                )
            );
        }
    };

    /** If admin option to edit  */
    const editDealerLocation = useCheckGroupNamePresent(enums.auth.group.admin)
        ? true
        : false;

    return auditImage ? (
        <>
            <Grid container>
                {auditImage.foundElsewhere && (
                    <>
                        <GridColumn
                            label="foundInBranch"
                            value={foundElsewhere.branchName}
                        />
                        <GridColumn
                            label="foundDate"
                            value={format.dateTimeNew(
                                foundElsewhere.createdDate
                            )}
                        />
                        <GridColumn
                            label="foundBy"
                            value={
                                foundElsewhere.foundByKey
                                    ? strings(foundElsewhere.foundByKey)
                                    : foundElsewhere.foundBy
                            }
                        />
                    </>
                )}
                {!auditImage.foundElsewhere && (
                    <GridColumn
                        label="time"
                        value={format.dateTimeNew(createdDate)}
                    />
                )}
                <GridColumn label="imageType" value={imageTypeName} />
                {closestLocation.distance !== undefined ? (
                    <GridColumn
                        label="locations"
                        value={
                            closestLocation.geo &&
                            closestLocation.geo[0].latitude &&
                            closestLocation.geo[0].longitude ? (
                                <Link
                                    href={helpers.googleMapsSrcDesUrl(
                                        closestLocation.geo[0].latitude,
                                        closestLocation.geo[0].longitude,
                                        latitude,
                                        longitude
                                    )}
                                    target="_blank"
                                    className={
                                        closestLocation.distance <
                                        allowedDistanceRange
                                            ? classes.success
                                            : classes.failure
                                    }
                                    underline="hover"
                                >
                                    {closestLocation.distance.toFixed(2)} km
                                </Link>
                            ) : null
                        }
                    />
                ) : null}

                {/* 
                    Ernest Detail
                */}
                <ErnestDetail services={services} auditImageId={id} />

                {services && displayServices ? (
                    <AuditServices serviceResult={services} />
                ) : null}

                <Grid item xs={12}>
                    <Typography
                        variant="caption"
                        className={classes.columnLabelLink}
                        onClick={
                            editDealerLocation ? onUpdateDealerLocation : null
                        }
                    >
                        {editDealerLocation ? (
                            <Edit
                                fontSize="small"
                                color="primary"
                                className={classes.editIcon}
                            />
                        ) : null}
                        {strings("dealerLocation")}:
                    </Typography>
                    <Typography variant="caption">
                        <Link
                            href={helpers.googleMapsUrl(branchAddress)}
                            target="_blank"
                            underline="hover"
                        >
                            {branchAddress}
                        </Link>
                    </Typography>
                </Grid>
            </Grid>

            <Dialog open={locationDialog} onClose={onCloseDealerLocation}>
                <DialogContent>
                    <UpdateGeoLocation
                        onClose={onCloseDealerLocation}
                        imageGeoLocation={{
                            latitude,
                            longitude,
                            location: 0,
                            id: id
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    ) : null;
};
